/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/naming-convention */
import React, { ReactNode, useState, useContext } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Row, Col, Input } from 'antd';
import { DeleteOutlined, PlusCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useFormContext, Controller } from 'react-hook-form';
import BUILD_PAYMENT_COD_QUERY, {
  BuildPaymentCodData,
  BuildPaymentCodVars,
  ShipmentList,
} from 'shared/graphql/query/buildPaymentNewCod';
import formatCurrency from 'shared/utils/formatCurrency';
import { formatDate } from 'shared/utils/dateTime';
import { Button, Text, Spin } from 'shared/components';
import { FormValues } from 'views/CashOnDelivery/Routes/CashOnDeliveryByShipment/Routes/Cod/ModalCreatePaymentCod/model/formValues.model';
import {
  CodContext,
  SelectedCodItemType,
} from 'views/CashOnDelivery/Routes/CashOnDeliveryByShipment/common/context/CodContext';
import ModalAddCod from '../ModalAddCod';
import { StyledTable, StyledFormItem } from './Styles';
import ExpandedRow from './ExpandedRow';

const { TextArea } = Input;

type CodDataTable = {
  key: string;
  tools: ReactNode;
  column1: string;
  column2: ReactNode;
  column3: string;
  column4: string;
  shipmentList: ShipmentList[];
};

const columns = [
  {
    title: 'เครื่องมือ',
    dataIndex: 'tools',
    width: 70,
  },
  {
    title: 'รหัส COD-IN',
    dataIndex: 'column1',
    width: 160,
  },
  {
    title: 'ยอดรวม COD (บาท)',
    dataIndex: 'column2',
    width: 160,
    align: 'right',
  },
  {
    title: 'วันที่แจ้งโอน',
    dataIndex: 'column3',
    width: 160,
  },
  {
    title: 'วันที่อัพเดต',
    dataIndex: 'column4',
    width: 160,
  },
  {
    title: '',
  },
];

const CodList: React.FC = () => {
  const { control } = useFormContext<FormValues>();
  const [isOpenModalAddCod, setOpenModalAddCod] = useState<boolean>(false);
  const { selectedCodItems, setSelectedCodItems } = useContext(CodContext);

  const { error, data, loading } = useQuery<BuildPaymentCodData, BuildPaymentCodVars>(
    BUILD_PAYMENT_COD_QUERY,
    {
      variables: {
        cod_item: selectedCodItems.codItemsId,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  let content = <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />;
  let codDataTable: CodDataTable[] = [];

  if (data?.buildPaymentNewCod) {
    const { cod_item } = data.buildPaymentNewCod;

    codDataTable = cod_item.map((item) => {
      return {
        key: item._id,
        tools: (
          <Button
            disabled={selectedCodItems.codItemsId.length < 2}
            title="ลบ"
            color="red--outline"
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => {
              const filterInvoice = selectedCodItems.codItemsId.filter(
                (value) => value !== item._id,
              );
              setSelectedCodItems(
                (prevState): SelectedCodItemType => ({
                  ...prevState,
                  codItemsId: filterInvoice,
                }),
              );
            }}
          />
        ),
        column1: item.cod_in_number || '-',
        column2: (
          <Text customType="primary" strong>
            {formatCurrency(item.total_cod)}
          </Text>
        ),
        column3: formatDate(item.cod_in_date) || '-',
        column4: formatDate(item.last_update) || '-',
        shipmentList: item.shipment_list,
      };
    });

    content = (
      <>
        <StyledTable
          scroll={{ x: 'max-content' }}
          pagination={false}
          columns={columns}
          dataSource={codDataTable}
          size="small"
          expandable={{
            expandedRowRender: (record: { shipmentList: ShipmentList[] }) => (
              <ExpandedRow data={record.shipmentList} />
            ),
          }}
          loading={loading}
        />
        <Row justify="space-between" style={{ marginTop: 10 }}>
          <Col>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Button
                  color="blue--light"
                  icon={<PlusCircleOutlined />}
                  onClick={() => setOpenModalAddCod(true)}
                >
                  เพิ่มรายการ
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            หมายเหตุ <Text customType="grey">(ไม่บังคับ)</Text>
            <StyledFormItem>
              <Controller
                control={control}
                name="paymentCodRemark"
                render={({ onChange, value }) => (
                  <TextArea
                    value={value || undefined}
                    placeholder="ระบุ"
                    rows={4}
                    onChange={(event) => {
                      onChange && onChange(event.target.value);
                    }}
                  />
                )}
              />
            </StyledFormItem>
          </Col>
        </Row>

        <ModalAddCod
          visible={isOpenModalAddCod}
          onOk={(cods) => {
            setSelectedCodItems(
              (prevState): SelectedCodItemType => ({
                ...prevState,
                codItemsId: cods.map((value) => value.id),
              }),
            );
            setOpenModalAddCod(false);
          }}
          onCancel={() => setOpenModalAddCod(false)}
          cods={cod_item.map((codItem) => ({
            id: codItem._id,
          }))}
        />
      </>
    );
  }

  if (error) {
    content = <div>{error.message}</div>;
  }

  return content;
};

export default CodList;
